import { useEffect, useState } from 'react';
import { addBranch, updateBranch, deleteBranch, Branch } from '../api';
import { api } from '../api/client';
import Modal from '../components/common/Modal';
import '../components/common/Modal.css';
import ConfirmDialog from '../components/common/ConfirmDialog';
import Spinner from '../components/common/Spinner';
import './Page.css';

const Branches = () => {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingBranch, setEditingBranch] = useState<Branch | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Branch | null>(null);

  const [name, setName] = useState('');
  const [editName, setEditName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const load = () => {
    setLoading(true);
    api.get<Branch[]>('/admin/branches')
      .then(setBranches)
      .catch(() => setBranches([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async (e: { preventDefault(): void }) => {
    e.preventDefault();
    setFormError('');
    setSubmitting(true);
    try {
      await addBranch({ name });
      setShowAddModal(false);
      setName('');
      load();
    } catch (e: unknown) {
      setFormError(e instanceof Error ? e.message : 'Failed to add branch');
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = (branch: Branch) => {
    setEditingBranch(branch);
    setEditName(branch.name);
    setFormError('');
    setShowEditModal(true);
  };

  const handleEdit = async (e: { preventDefault(): void }) => {
    e.preventDefault();
    if (!editingBranch) return;
    setFormError('');
    setSubmitting(true);
    try {
      await updateBranch(editingBranch._id, { name: editName });
      setShowEditModal(false);
      setEditingBranch(null);
      load();
    } catch (e: unknown) {
      setFormError(e instanceof Error ? e.message : 'Failed to update branch');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteBranch(confirmDelete._id);
      setConfirmDelete(null);
      load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to delete branch');
      setConfirmDelete(null);
    }
  };

  const filtered = branches.filter((b) =>
    b.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Branches</h1>
          <p className="page-subtitle">{branches.length} branch{branches.length !== 1 ? 'es' : ''} defined</p>
        </div>
        <button className="btn-primary" onClick={() => { setName(''); setFormError(''); setShowAddModal(true); }}>
          + Add Branch
        </button>
      </div>

      <div className="page-toolbar">
        <input
          className="search-input"
          placeholder="Search branches..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loading && <Spinner />}
      {error && <p className="error-banner">{error}</p>}

      {!loading && (
        <div className="table-card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Branch Name</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={3} className="empty-cell">No branches found</td></tr>
              ) : (
                filtered.map((b) => (
                  <tr key={b._id}>
                    <td><strong>{b.name}</strong></td>
                    <td>{new Date(b.createdAt).toLocaleDateString('en-GB')}</td>
                    <td className="action-cell">
                      <button className="btn-sm" onClick={() => openEdit(b)}>Edit</button>
                      <button className="btn-sm btn-danger" style={{ marginLeft: 8 }} onClick={() => setConfirmDelete(b)}>Delete</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {showAddModal && (
        <Modal title="Add Branch" onClose={() => setShowAddModal(false)}>
          <form onSubmit={handleAdd}>
            {formError && <p className="form-error">{formError}</p>}
            <div className="form-field">
              <label>Branch Name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Cairo HQ, Alexandria"
                required
                autoFocus
              />
            </div>
            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={submitting}>{submitting ? 'Saving...' : 'Add Branch'}</button>
            </div>
          </form>
        </Modal>
      )}

      {showEditModal && editingBranch && (
        <Modal title={`Edit — ${editingBranch.name}`} onClose={() => setShowEditModal(false)}>
          <form onSubmit={handleEdit}>
            {formError && <p className="form-error">{formError}</p>}
            <div className="form-field">
              <label>Branch Name</label>
              <input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={submitting}>{submitting ? 'Saving...' : 'Save Changes'}</button>
            </div>
          </form>
        </Modal>
      )}

      {confirmDelete && (
        <ConfirmDialog
          title="Delete Branch"
          message={`Are you sure you want to delete "${confirmDelete.name}"? This action cannot be undone.`}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
};

export default Branches;
